export type Grade = 'S' | 'A' | 'B' | 'C' | 'D' | 'F'

export const GRADE_ORDER: Grade[] = ['S', 'A', 'B', 'C', 'D', 'F']

export type RunHistory = {
  runsCompleted: number
  lastGrade: Grade
  bestGrade: Grade
  worstGrade: Grade
}

const STORAGE_KEY = 'trollilopolis:runs:v1'

const isGrade = (value: unknown): value is Grade =>
  typeof value === 'string' && (GRADE_ORDER as string[]).includes(value)

/** Lower index = better grade. */
const better = (a: Grade, b: Grade): Grade =>
  GRADE_ORDER.indexOf(a) <= GRADE_ORDER.indexOf(b) ? a : b
const worse = (a: Grade, b: Grade): Grade =>
  GRADE_ORDER.indexOf(a) >= GRADE_ORDER.indexOf(b) ? a : b

/**
 * Remembers completed runs per browser so the splash can say "Last run: F"
 * and a returning player has a grade to beat. Separate key from the
 * per-victim stats so neither blob can corrupt the other.
 */
export class RunsService {
  public load(): RunHistory | null {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as Partial<RunHistory> | null
      if (
        parsed &&
        Number.isFinite(parsed.runsCompleted) &&
        isGrade(parsed.lastGrade) &&
        isGrade(parsed.bestGrade) &&
        isGrade(parsed.worstGrade)
      ) {
        return {
          runsCompleted: parsed.runsCompleted as number,
          lastGrade: parsed.lastGrade,
          bestGrade: parsed.bestGrade,
          worstGrade: parsed.worstGrade,
        }
      }
    } catch (error) {
      console.warn('Could not load run history:', error)
    }
    return null
  }

  public record(grade: Grade): RunHistory {
    const previous = this.load()
    const next: RunHistory = previous
      ? {
          runsCompleted: previous.runsCompleted + 1,
          lastGrade: grade,
          bestGrade: better(previous.bestGrade, grade),
          worstGrade: worse(previous.worstGrade, grade),
        }
      : { runsCompleted: 1, lastGrade: grade, bestGrade: grade, worstGrade: grade }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch (error) {
      console.warn('Could not persist run history:', error)
    }
    return next
  }
}

export const runsService = new RunsService()
